/**
 * SubjectDetail — EntityDetailShell-based Subject detail page (11E.4).
 */

import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '@/components/common/Card';
import { PageContainer } from '@/components/layout';
import { EntityDetailShell } from '@/components/entity';
import { useSubject } from '@features/catalog/api/subjects';
import { SubjectFormModal } from '@features/catalog/components/SubjectFormModal';
import { buildSubjectDetailConfig } from '@features/subjects/config/subjectsEntityConfig';
import { formatPaise } from '@features/catalog/config/ui';

export const SubjectDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { data: subject, isLoading, isError } = useSubject(id ?? '');

  if (isLoading) {
    return (
      <PageContainer title="Loading">
        <Card padding="lg"><p style={{ color: '#64748b' }}>Loading subject…</p></Card>
      </PageContainer>
    );
  }

  if (isError || !subject) {
    return (
      <PageContainer title="Subject Not Found">
        <Card padding="lg"><p>The subject you are looking for does not exist.</p></Card>
      </PageContainer>
    );
  }

  const overviewContent = (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: 24,
      }}
    >
      <Field label="Category" value={subject.category} />
      <Field label="Duration" value={`${subject.duration_minutes} min`} />
      <Field label="Price / session" value={formatPaise(subject.price_per_session)} />
    </div>
  );

  const detailConfig = buildSubjectDetailConfig({
    renderOverviewTab: () => overviewContent,
  });

  return (
    <>
      <EntityDetailShell
        entity={subject}
        config={detailConfig}
        onEdit={() => setIsEditOpen(true)}
        canEdit
      />
      <SubjectFormModal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        subject={subject}
      />
    </>
  );
};

const Field: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <div style={{ fontSize: 12, color: '#64748b', marginBottom: 2 }}>{label}</div>
    <div style={{ fontSize: 14, fontWeight: 500 }}>{value}</div>
  </div>
);

